import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Link from '@mui/material/Link';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import Skeleton from '@mui/material/Skeleton';
import IconButton from '@mui/material/IconButton';
import { alpha, useTheme } from '@mui/material/styles';
import { shadow } from '../../theme';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import HealthAndSafetyOutlinedIcon from '@mui/icons-material/HealthAndSafetyOutlined';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import CloudCircleOutlinedIcon from '@mui/icons-material/CloudCircleOutlined';
import DonutSmallOutlinedIcon from '@mui/icons-material/DonutSmallOutlined';
import PlaylistAddCheckCircleOutlinedIcon from '@mui/icons-material/PlaylistAddCheckCircleOutlined';
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';
import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined';
import BuildCircleOutlinedIcon from '@mui/icons-material/BuildCircleOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

import { useAuth } from '../../Context/AuthenticationContext';
import { hasAccess } from '../../Utils/roles';
import ServicesMonitorService from '../../Services/ServicesMonitorService';
import WebScraperService from '../../Services/WebScraperService';
import { SHOW_BUGNION, SHOW_SERVICES_MONITOR, SHOW_SMSC_CLIENT_CONFIG } from '../../config';
import authBackground from '../../assets/brand/clarivate-auth-background.jpg';
import logoWhite from '../../assets/brand/clarivate-logo-horizontal-white.svg';
import securityPictogram from '../../assets/brand/clarivate-pictogram-security.svg';

const SUPPORT_URL = 'https://clarivate.service-now.com/';

function useStatusColors() {
  const theme = useTheme();
  return {
    ACCENT: theme.tokens.accent,
    SUCCESS: theme.tokens.success,
    WARNING: theme.tokens.warning,
    ERROR: theme.tokens.danger,
    NEUTRAL: theme.tokens.fgTertiary,
  };
}

type StatusColors = ReturnType<typeof useStatusColors>;

const HEALTH_SERVICES: { id: string; label: string }[] = [
  { id: 'cspservice', label: 'CSP' },
  { id: 'adv', label: 'ADV' },
  { id: 'webscraper-node', label: 'Web Scraper' },
  { id: 'ihubadapter', label: 'iHub Adapter' },
  { id: 'bugnioninterface', label: 'Bugnion' },
  { id: 'uaa', label: 'User Management' },
];

function greetingFor(hour: number): string {
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

function StatusDot({ color, size = 10 }: { color: string; size?: number }) {
  return (
    <span
      style={{
        display: 'inline-block',
        width: size,
        height: size,
        borderRadius: '50%',
        backgroundColor: color,
        flexShrink: 0,
        boxShadow: `0 0 0 3px ${alpha(color, 0.13)}`,
      }}
    />
  );
}

interface KpiTileProps {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  sublabel?: string;
  accent: string;
  loading?: boolean;
  noPermission?: boolean;
}

function KpiTile({ icon, label, value, sublabel, accent, loading, noPermission }: KpiTileProps) {
  const { NEUTRAL } = useStatusColors();
  const effectiveAccent = noPermission ? NEUTRAL : accent;
  return (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        position: 'relative',
        overflow: 'hidden',
        opacity: noPermission ? 0.65 : 1,
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          backgroundColor: effectiveAccent,
        },
        transition: 'box-shadow 0.18s ease, transform 0.18s ease',
        '&:hover': noPermission
          ? {}
          : {
              boxShadow: shadow.md,
              transform: 'translateY(-1px)',
            },
      }}
    >
      <CardContent sx={{ pt: 3, pb: 2.5, px: 2.5 }}>
        <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 1.5 }}>
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1.5,
              backgroundColor: alpha(effectiveAccent, 0.1),
              color: effectiveAccent,
            }}
          >
            {noPermission ? <LockOutlinedIcon fontSize="small" /> : icon}
          </Stack>
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ letterSpacing: '0.06em', lineHeight: 1.2 }}
          >
            {label}
          </Typography>
        </Stack>
        <Typography
          variant="h3"
          sx={{
            fontWeight: 700,
            lineHeight: 1.1,
            fontVariantNumeric: 'tabular-nums',
            color: effectiveAccent,
            mb: 0.75,
          }}
        >
          {noPermission ? '-' : loading ? <Skeleton width={60} /> : value}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {noPermission ? 'No access' : sublabel}
        </Typography>
      </CardContent>
    </Card>
  );
}

interface HealthRow {
  label: string;
  status: 'up' | 'down' | 'unknown';
}

interface WsTotals {
  inProgress: number;
  done: number;
  failed: number;
  canceled: number;
  all: number;
}

interface WsJob {
  id: string;
  fileName: string;
  country: string;
  status: string;
  createdAt?: string;
}

function NoPermissionBody({ message }: { message: string }) {
  return (
    <Stack
      direction="row"
      spacing={1.25}
      alignItems="center"
      sx={{
        py: 2,
        px: 1.5,
        borderRadius: 1,
        backgroundColor: 'rgba(0,0,0,0.025)',
        border: '1px dashed rgba(0,0,0,0.08)',
      }}
    >
      <LockOutlinedIcon fontSize="small" sx={{ color: 'text.secondary', opacity: 0.7 }} />
      <Typography variant="body2" color="text.secondary">
        {message}
      </Typography>
    </Stack>
  );
}

function relativeTime(iso?: string): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diffMs = Date.now() - then;
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function ServicesHealthCard({
  rows,
  loading,
  noPermission,
  onRefresh,
  refreshing,
}: {
  rows: HealthRow[];
  loading: boolean;
  noPermission: boolean;
  onRefresh: () => void;
  refreshing: boolean;
}) {
  const { ACCENT, SUCCESS, ERROR, NEUTRAL } = useStatusColors();
  const upCount = rows.filter(r => r.status === 'up').length;
  const downCount = rows.filter(r => r.status === 'down').length;
  const totalCount = rows.length || HEALTH_SERVICES.length;

  if (noPermission) {
    return (
      <Card variant="outlined" sx={{ height: '100%', opacity: 0.7 }}>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
            <HealthAndSafetyOutlinedIcon sx={{ color: 'text.secondary' }} fontSize="small" />
            <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.secondary' }}>
              Services health
            </Typography>
          </Stack>
          <NoPermissionBody message="You do not have access to Services Monitor. Contact your administrator to view service health." />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <HealthAndSafetyOutlinedIcon sx={{ color: ACCENT }} fontSize="small" />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Services health
            </Typography>
          </Stack>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={onRefresh} disabled={refreshing}>
              <RefreshIcon
                fontSize="small"
                sx={{
                  color: ACCENT,
                  animation: refreshing ? 'spin 1s linear infinite' : 'none',
                  '@keyframes spin': { to: { transform: 'rotate(360deg)' } },
                }}
              />
            </IconButton>
          </Tooltip>
        </Stack>

        <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 2 }}>
          <Typography
            variant="h3"
            sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: SUCCESS }}
          >
            {loading ? <Skeleton width={70} /> : `${upCount}`}
          </Typography>
          <Typography
            variant="h5"
            color="text.secondary"
            sx={{ fontVariantNumeric: 'tabular-nums' }}
          >
            / {totalCount}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            healthy
          </Typography>
          {downCount > 0 && (
            <Chip
              size="small"
              label={`${downCount} down`}
              sx={{
                ml: 1,
                backgroundColor: alpha(ERROR, 0.13),
                color: ERROR,
                fontWeight: 600,
                fontSize: 11,
              }}
            />
          )}
        </Stack>

        <Divider sx={{ mb: 1.5 }} />

        <Stack spacing={1}>
          {(loading
            ? HEALTH_SERVICES.map(s => ({ label: s.label, status: 'unknown' as const }))
            : rows
          ).map(r => (
            <Stack key={r.label} direction="row" alignItems="center" justifyContent="space-between">
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <StatusDot
                  color={r.status === 'up' ? SUCCESS : r.status === 'down' ? ERROR : NEUTRAL}
                />
                <Typography variant="body2">{r.label}</Typography>
              </Stack>
              <Typography
                variant="caption"
                sx={{
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  fontWeight: 600,
                  color:
                    r.status === 'up' ? SUCCESS : r.status === 'down' ? ERROR : 'text.secondary',
                }}
              >
                {loading ? (
                  <Skeleton width={50} />
                ) : r.status === 'up' ? (
                  'Up'
                ) : r.status === 'down' ? (
                  'Down'
                ) : (
                  'Unknown'
                )}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
}

function statusColor(status: string, c: StatusColors): string {
  const s = status.toLowerCase();
  if (s.includes('done')) return c.SUCCESS;
  if (s.includes('progress')) return c.ACCENT;
  if (s.includes('fail') || s.includes('error')) return c.ERROR;
  if (s.includes('cancel')) return c.NEUTRAL;
  if (s.includes('pause')) return c.WARNING;
  return c.NEUTRAL;
}

function WebScraperCard({
  totals,
  loading,
  recent,
  recentLoading,
  noPermission,
}: {
  totals: WsTotals | null;
  loading: boolean;
  recent: WsJob[];
  recentLoading: boolean;
  noPermission: boolean;
}) {
  const colors = useStatusColors();
  const { ACCENT, SUCCESS, ERROR, NEUTRAL } = colors;
  const rows = [
    { label: 'In progress', value: totals?.inProgress ?? 0, color: ACCENT },
    { label: 'Done', value: totals?.done ?? 0, color: SUCCESS },
    { label: 'Failed', value: totals?.failed ?? 0, color: ERROR },
    { label: 'Canceled', value: totals?.canceled ?? 0, color: NEUTRAL },
  ];

  if (noPermission) {
    return (
      <Card variant="outlined" sx={{ height: '100%', opacity: 0.7 }}>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
            <LanguageOutlinedIcon sx={{ color: 'text.secondary' }} fontSize="small" />
            <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.secondary' }}>
              Web Scraper
            </Typography>
          </Stack>
          <NoPermissionBody message="You do not have access to Web Scraper. Contact your administrator to view scraping jobs." />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <LanguageOutlinedIcon sx={{ color: ACCENT }} fontSize="small" />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Web Scraper
            </Typography>
          </Stack>
          <Link
            component={RouterLink}
            to="/component_webscraper?tab=monitor"
            underline="none"
            sx={{
              fontSize: 13,
              fontWeight: 600,
              color: ACCENT,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            View all <ArrowForwardIcon fontSize="inherit" />
          </Link>
        </Stack>

        <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 2 }}>
          <Typography
            variant="h3"
            sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: ACCENT }}
          >
            {loading ? <Skeleton width={70} /> : (totals?.all ?? 0)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            total jobs
          </Typography>
        </Stack>

        <Grid container spacing={1} sx={{ mb: 2 }}>
          {rows.map(r => (
            <Grid size={6} key={r.label}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <StatusDot color={r.color} size={8} />
                <Typography variant="body2" color="text.secondary">
                  {r.label}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 700,
                    ml: 'auto',
                    fontVariantNumeric: 'tabular-nums',
                    color: r.color,
                  }}
                >
                  {loading ? '-' : r.value}
                </Typography>
              </Stack>
            </Grid>
          ))}
        </Grid>

        <Divider sx={{ mb: 1.5 }} />

        <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: '0.06em' }}>
          In progress now
        </Typography>
        <Stack spacing={1} sx={{ mt: 1 }}>
          {recentLoading && (
            <>
              <Skeleton variant="rounded" height={32} />
              <Skeleton variant="rounded" height={32} />
              <Skeleton variant="rounded" height={32} />
            </>
          )}
          {!recentLoading && recent.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No jobs running right now.
            </Typography>
          )}
          {!recentLoading &&
            recent.map(job => (
              <Stack
                key={job.id}
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={1}
              >
                <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
                  <StatusDot color={statusColor(job.status, colors)} size={8} />
                  <Typography
                    variant="body2"
                    sx={{
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxWidth: 220,
                    }}
                  >
                    {job.fileName || job.id}
                  </Typography>
                  {job.country && (
                    <Chip
                      size="small"
                      label={job.country}
                      sx={{
                        height: 18,
                        fontSize: 11,
                        backgroundColor: alpha(ACCENT, 0.1),
                        color: ACCENT,
                      }}
                    />
                  )}
                </Stack>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <AccessTimeIcon sx={{ fontSize: 12, color: 'text.disabled' }} />
                  <Typography variant="caption" color="text.secondary">
                    {relativeTime(job.createdAt) || '-'}
                  </Typography>
                </Stack>
              </Stack>
            ))}
        </Stack>
      </CardContent>
    </Card>
  );
}

interface QuickAction {
  label: string;
  to: string;
  icon: React.ReactNode;
  roles: string[];
}

function QuickActions({ roles }: { roles: string[] }) {
  const { ACCENT } = useStatusColors();
  const actions: (QuickAction & { show?: boolean })[] = [
    {
      label: 'Web Scraper',
      to: '/component_webscraper',
      icon: <LanguageOutlinedIcon fontSize="small" />,
      roles: ['WEB_SCRAPER'],
    },
    {
      label: 'iHub Adapter',
      to: '/ihub_adapter',
      icon: <CloudCircleOutlinedIcon fontSize="small" />,
      roles: ['IHUB_CUSTOMER_MIGRATION'],
    },
    {
      label: 'Batch ID',
      to: '/batchid',
      icon: <DonutSmallOutlinedIcon fontSize="small" />,
      roles: ['AMS_PAYMENTS', 'PRS_PAYMENTS', 'TM1_PAYMENTS'],
    },
    {
      label: 'Bugnion',
      to: '/bugnion',
      icon: <PlaylistAddCheckCircleOutlinedIcon fontSize="small" />,
      roles: ['BUGNION_INTERFACE'],
      show: SHOW_BUGNION,
    },
    {
      label: 'User Management',
      to: '/user_management',
      icon: <PeopleOutlinedIcon fontSize="small" />,
      roles: ['USER_MANAGEMENT'],
    },
    {
      label: 'Services Monitor',
      to: '/services_monitor',
      icon: <MonitorHeartOutlinedIcon fontSize="small" />,
      roles: ['ROLE_SERVICES_MONITOR'],
      show: SHOW_SERVICES_MONITOR,
    },
    {
      label: 'SMSC Config',
      to: '/twindolphin_onboarding',
      icon: <BuildCircleOutlinedIcon fontSize="small" />,
      roles: ['ROLE_SMSC_ADMIN'],
      show: SHOW_SMSC_CLIENT_CONFIG,
    },
  ];

  const visible = actions.filter(a => {
    if (a.show === false) return false;
    return a.roles.some(r => roles.includes(r));
  });

  if (visible.length === 0) return null;

  return (
    <Stack spacing={1.5}>
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ letterSpacing: '0.08em', fontWeight: 700 }}
      >
        Quick actions
      </Typography>
      <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1 }}>
        {visible.map(a => (
          <Button
            key={a.to}
            component={RouterLink}
            to={a.to}
            variant="outlined"
            size="small"
            startIcon={a.icon}
            sx={{
              textTransform: 'none',
              borderColor: alpha(ACCENT, 0.25),
              color: ACCENT,
              fontWeight: 600,
              py: 0.75,
              px: 1.5,
              '&:hover': { borderColor: ACCENT, backgroundColor: alpha(ACCENT, 0.04) },
            }}
          >
            {a.label}
          </Button>
        ))}
      </Stack>
    </Stack>
  );
}

function useServicesHealth(enabled: boolean, hasRole: boolean) {
  const [rows, setRows] = useState<HealthRow[]>([]);
  const [loading, setLoading] = useState(enabled && hasRole);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!enabled || !hasRole) {
      setLoading(false);
      setRows([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    Promise.allSettled(HEALTH_SERVICES.map(s => ServicesMonitorService.getHealth(s.id))).then(
      results => {
        if (cancelled) return;
        const mapped: HealthRow[] = results.map((res, idx) => {
          const label = HEALTH_SERVICES[idx]?.label ?? '';
          if (res.status !== 'fulfilled') return { label, status: 'unknown' };
          const s = String(res.value?.data?.status || '').toUpperCase();
          if (s === 'UP') return { label, status: 'up' };
          if (s === 'DOWN' || s === 'OUT_OF_SERVICE') return { label, status: 'down' };
          return { label, status: 'unknown' };
        });
        setRows(mapped);
        setLoading(false);
      }
    );
    return () => {
      cancelled = true;
    };
  }, [tick, enabled, hasRole]);

  return {
    rows,
    loading,
    noPermission: !hasRole,
    refresh: () => setTick(t => t + 1),
  };
}

function useWebScraperData(enabled: boolean, hasRole: boolean) {
  const [totals, setTotals] = useState<WsTotals | null>(null);
  const [totalsLoading, setTotalsLoading] = useState(enabled && hasRole);
  const [recent, setRecent] = useState<WsJob[]>([]);
  const [recentLoading, setRecentLoading] = useState(enabled && hasRole);

  useEffect(() => {
    if (!enabled || !hasRole) {
      setTotalsLoading(false);
      setRecentLoading(false);
      return;
    }
    let cancelled = false;

    WebScraperService.getScrapingStatusTotals()
      .then(res => {
        if (cancelled) return;
        const data = res?.data || {};
        const t = data?.totals || data || {};
        setTotals({
          all: (typeof data?.totalSize === 'number' && data.totalSize) || t.all || 0,
          inProgress: t['In progress'] ?? t.inProgress ?? 0,
          done: t.Done ?? t.done ?? 0,
          failed: t.Failed ?? t.failed ?? 0,
          canceled: t.Canceled ?? t.canceled ?? 0,
        });
      })
      .catch(() => {
        if (!cancelled) setTotals(null);
      })
      .finally(() => {
        if (!cancelled) setTotalsLoading(false);
      });

    WebScraperService.getAllInProgress(0, 5, 'createdAt', 'desc')
      .then(res => {
        if (cancelled) return;
        const list = res?.data?.content || res?.data?.items || res?.data || [];
        const mapped: WsJob[] = (Array.isArray(list) ? list : [])
          .slice(0, 5)
          .map((j: Record<string, unknown>) => ({
            id: String(j.id ?? j.jobId ?? Math.random()),
            fileName: String(j.fileName || j.name || j.file || ''),
            country: String(j.country || j.countryCode || ''),
            status: String(j.status || 'In progress'),
            createdAt: (j.createdAt || j.startedAt || j.timestamp) as string | undefined,
          }));
        setRecent(mapped);
      })
      .catch(() => {
        if (!cancelled) setRecent([]);
      })
      .finally(() => {
        if (!cancelled) setRecentLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, hasRole]);

  return {
    totals,
    totalsLoading,
    recent,
    recentLoading,
    noPermission: !hasRole,
  };
}

export default function Landing() {
  const { ACCENT, SUCCESS, ERROR, NEUTRAL } = useStatusColors();
  const { isUserAuthenticated, login, user } = useAuth();
  const isAuthenticated = isUserAuthenticated();

  const handleLogin = async () => {
    await login();
  };

  const userRoles = user?.roles;
  const canViewServicesHealth = hasAccess(userRoles, ['ROLE_SERVICES_MONITOR']);
  const canViewWebScraper = hasAccess(userRoles, ['WEB_SCRAPER']);

  const health = useServicesHealth(isAuthenticated, canViewServicesHealth);
  const ws = useWebScraperData(isAuthenticated, canViewWebScraper);

  const kpis = useMemo(() => {
    const upCount = health.rows.filter(r => r.status === 'up').length;
    const total = health.rows.length || HEALTH_SERVICES.length;
    return {
      inProgress: ws.totals?.inProgress ?? 0,
      failed: ws.totals?.failed ?? 0,
      done: ws.totals?.done ?? 0,
      healthy: `${upCount}/${total}`,
    };
  }, [health.rows, ws.totals]);

  // Authenticated users see the Operations Overview
  if (isAuthenticated) {
    const now = new Date();
    const greeting = greetingFor(now.getHours());
    const rawName = (user?.displayName || '').trim();
    // Support "First Last" and "Last, First" formats
    const normalized = rawName.includes(',')
      ? rawName
          .split(',')
          .map(p => p.trim())
          .reverse()
          .join(' ')
      : rawName;
    const firstName = (normalized.split(/\s+/)[0] || '').replace(/[^\p{L}\p{M}'-]/gu, '');
    const dateLabel = now.toLocaleDateString('en-US', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
    const roles = user?.roles || [];

    const kpisLoading = ws.totalsLoading;

    return (
      <div className="lp-home">
        <div className="lp-home-header">
          <div className="lp-home-content">
            <h1 id="landing-welcome-title" className="lp-home-title">
              {greeting}
              {firstName ? `, ${firstName}` : ''}
            </h1>
            <p className="lp-home-sub">Here&apos;s what&apos;s happening in your operations.</p>
          </div>
          <span className="lp-home-date">{dateLabel}</span>
        </div>

        <Grid container spacing={2.5} sx={{ mt: 2 }}>
          <Grid size={{ xs: 6, md: 3 }}>
            <KpiTile
              icon={<PendingActionsIcon fontSize="small" />}
              label="In progress"
              value={kpis.inProgress}
              sublabel="scraping jobs running"
              accent={ACCENT}
              loading={kpisLoading}
              noPermission={!canViewWebScraper}
            />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <KpiTile
              icon={<ErrorOutlineIcon fontSize="small" />}
              label="Failed"
              value={kpis.failed}
              sublabel="needs attention"
              accent={kpis.failed > 0 ? ERROR : NEUTRAL}
              loading={kpisLoading}
              noPermission={!canViewWebScraper}
            />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <KpiTile
              icon={<CheckCircleIcon fontSize="small" />}
              label="Completed"
              value={kpis.done}
              sublabel="successful runs"
              accent={SUCCESS}
              loading={kpisLoading}
              noPermission={!canViewWebScraper}
            />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <KpiTile
              icon={<HealthAndSafetyOutlinedIcon fontSize="small" />}
              label="Services"
              value={kpis.healthy}
              sublabel="reporting healthy"
              accent={SUCCESS}
              loading={health.loading}
              noPermission={!canViewServicesHealth}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <ServicesHealthCard
              rows={health.rows}
              loading={health.loading}
              noPermission={health.noPermission}
              onRefresh={health.refresh}
              refreshing={health.loading}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <WebScraperCard
              totals={ws.totals}
              loading={ws.totalsLoading}
              recent={ws.recent}
              recentLoading={ws.recentLoading}
              noPermission={ws.noPermission}
            />
          </Grid>

          <Grid size={12}>
            <QuickActions roles={roles} />
          </Grid>
        </Grid>
      </div>
    );
  }

  // Public auth screen - full viewport dark layout
  return (
    <div className="lp-root" style={{ backgroundImage: `url(${authBackground})` }}>
      <div className="lp-bg-overlay" />

      <header className="lp-header">
        <img src={logoWhite} alt="Clarivate" className="lp-header-logo" />
      </header>

      <main className="lp-main">
        <div className="lp-card">
          <div className="lp-card-body">
            <img src={securityPictogram} alt="" aria-hidden="true" className="lp-card-icon" />

            <h1 id="landing-dashboard-title" className="lp-card-title">
              Integrated Services Dashboard
            </h1>

            <p id="landing-login-text" className="lp-card-sub">
              Sign in with your Clarivate account.
              <br />
              Your roles determine which services you can access.
            </p>

            <Button
              id="landing-signin-link"
              onClick={handleLogin}
              className="lp-card-cta"
              fullWidth
              disableRipple
            >
              Sign in
            </Button>
          </div>

          <p className="lp-card-help">
            Having trouble?{' '}
            <Link
              id="landing-support-link"
              href={SUPPORT_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="lp-card-help-link"
            >
              Contact support
            </Link>
          </p>
        </div>
      </main>

      <footer className="lp-footer">
        <span>© {new Date().getFullYear()} Clarivate. All rights reserved.</span>
      </footer>
    </div>
  );
}
